import React from 'react';
import { Typography, Paper, Box, Table, TableBody, TableRow, TableCell } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { formatDate } from '../util/helpers';
import { formatNumber } from '../util/helpers';
import QeeHintBox from './qeeHintBox';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        width: '100%', // Ensure the component expands to 100% of its parent's width
    },
    paper: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        boxShadow: theme.shadows[2],
        borderRadius: theme.shape.borderRadius,
        width: '100%', // Ensure the paper expands to 100% of its parent's width
    },
    subform: {
        padding: theme.spacing(2),
        marginTop: theme.spacing(0),
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
    },
    totalCost: {
        fontSize: '1.2rem',
        fontWeight: 600,
        paddingTop: theme.spacing(4),
		paddingLeft: theme.spacing(2),
    },
    subtitle: {
        marginTop: theme.spacing(1),
		paddingLeft: theme.spacing(2),		
        color: theme.palette.text.secondary,
    },	
    boldText: {
        fontWeight: 'bold',
    },
    costSection: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
    },
    // Adjusted styles for smaller screens
    tableRow: {
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            borderBottom: `1px solid ${theme.palette.divider}`, // Row separator
        },
    },
    tableCellSmall: {
        display: 'table-cell', // Default to table-cell behavior for large screens
        paddingBottom: theme.spacing(1),
        [theme.breakpoints.down('sm')]: { 
            display: 'block',
            width: '100%',  // Ensure full width on small screens
            padding: theme.spacing(1, 0),
            borderBottom: 'none',  // Remove vertical cell separators on small screens
        },
    }	
}));

const OfferPreview = ({ ord, sac }) => {
    const classes = useStyles();

    const {
        priceRule = 'flat',
        quoteType = 'binding_offer',
        priceDisplayType = 'brutto',
        materialCost = 0,
        laborCost = 0,
        otherCosts = 0,
        hourlyFee = 0,
        hours = 0,
        totalCost = 0,
        desc = '',
        dateLimit,
        creationDate,
    } = ord.offer || {};

    // Configuration objects for the fields to be displayed
    const config = [
        {
            label: 'Erstellt von:',
            value: sac?.sac_receiver_org_name,
            cond: !!sac?.sac_receiver_org_name,
        },	
        {
            label: 'Erstellt am:',
            value: formatDate(creationDate, 'synapcusDate'),
            cond: !!creationDate,
        },
        {
            label: 'Angebot gültig bis:',
            value: formatDate(dateLimit, 'synapcusDate'),
            cond: !!dateLimit,
        },
        {
            label: 'Preisregelung:',
            value: priceRule === 'flat' ? 'Gesamtpreis' : 'Preis pro Stunde',
            cond: !!priceRule,
        },
        {
            label: 'Angebotstyp:',
            value: quoteType === 'binding_offer' ? 'Festpreis' : 'Voranschlag',
            cond: !!quoteType,
        },
        {
            label: 'Preise angeben in:',
            value: priceDisplayType === 'brutto' ? 'Brutto' : 'Netto',
            cond: priceRule === 'flat',
        },
        {
            label: 'Materialkosten:',
            value: formatNumber(materialCost, 'EUR', 2),
            cond: priceRule === 'flat',
        },
        {
            label: 'Lohnkosten:',
            value: formatNumber(laborCost, 'EUR', 2),
            cond: priceRule === 'flat',
        },
        {
            label: 'Sonstige Kosten:',
            value: formatNumber(otherCosts, 'EUR', 2),
            cond: priceRule === 'flat',
        },
        {
            label: 'Stundensatz:',
            value: formatNumber(hourlyFee, 'EUR', 2),
            cond: priceRule === 'per_hour',
        },
        {
            label: 'Anzahl Stunden:',
            value: hours,
            cond: priceRule === 'per_hour',
        },
        {
            label: 'Nachricht:',
            sublabel: 'Begleitender Text zum Angebot',
            value: desc,
            cond: !!desc,
        },
    ];

    return (
		<>
			<Table>
				<TableBody>
					{config.map(
						(item, index) =>
							item.cond && (
								<TableRow key={index} className={classes.tableRow}>
									<TableCell component="th" scope="row" className={classes.tableCellSmall}>
										<Box>
											<Typography variant="lbl" className={classes.fieldLabel} display="block">
												{item.label}
											</Typography>
											{item.sublabel && (
												<Typography variant="caption" color="textSecondary" display="block">
													{item.sublabel}
												</Typography>
											)}
										</Box>
									</TableCell>
									<TableCell className={classes.tableCellSmall}>
										<Typography variant="body2">
											{item.value}
										</Typography>
									</TableCell>
								</TableRow>
							)
					)}
				</TableBody>
			</Table>
			<Typography variant="h6" className={classes.totalCost}>
				<strong>Gesamtpreis (brutto):</strong> <span className={classes.boldText}>{formatNumber(totalCost, 'EUR', 2)}</span>
			</Typography>
			<Typography className={classes.subtitle}>
				inkl. 19% MwSt.
			</Typography>
			
			{sac?.sac_is_admin || sac?.sac_is_receiver ? (
				<Box className={classes.costSection}>
					<Typography variant="subtitle1"><strong>Deine Propertec Kosten bei Kundeninteresse</strong></Typography>
					<Typography variant="h6" className={classes.boldText}>
						{formatNumber(2.00, 'EUR', 2)} (exkl. MwSt.)
					</Typography>
					<Typography variant="body2">
						Die Angebotserstellung ist kostenlos. Du zahlst nur, wenn der Kunde Interesse an deinem Angebot zeigt.
					</Typography>
					<QeeHintBox
						type="info"
						showShortText={true}
						plainText="Was bedeutet Kundeninteresse? Ein Kunde bekundet echtes Interesse an deinem Angebot, wenn er dir eine Nachricht sendet, dich anruft, oder deine Kontaktdaten anfordert. In diesem Moment entsteht ein verbindlicher Kontakt, und erst dann fallen für dich die Propertec-Kosten an."
					/>
				</Box>
			) : null}
		</>
    );
};

export default OfferPreview;
